<template>
  <div>
    <Head title="Panel de Control" />

    <!-- ══ SKELETON LOADER (visible mientras Inertia carga) ══ -->
    <div v-if="isLoading" class="animate-pulse space-y-8">
      <!-- Header skeleton -->
      <div class="flex items-center gap-4 mb-8">
        <div class="h-8 bg-gray-200 rounded-lg w-48" />
        <div class="h-7 bg-gray-100 rounded-full w-24" />
        <div class="h-7 bg-gray-100 rounded-full w-20" />
      </div>

      <!-- Stat cards skeleton -->
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <div v-for="i in 4" :key="i" class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div class="w-10 h-10 rounded-lg bg-gray-200 mb-4" />
          <div class="h-3 bg-gray-200 rounded w-3/4 mb-3" />
          <div class="h-8 bg-gray-200 rounded w-1/2" />
        </div>
      </div>

      <!-- Financial summary skeleton -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div v-for="i in 3" :key="i" class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div class="w-10 h-10 rounded-lg bg-gray-200 mb-4" />
          <div class="h-3 bg-gray-200 rounded w-1/2 mb-3" />
          <div class="h-8 bg-gray-200 rounded w-2/3" />
        </div>
      </div>

      <!-- Table skeleton -->
      <div class="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div class="p-6 border-b border-gray-50">
          <div class="h-5 bg-gray-200 rounded w-32" />
        </div>
        <div class="divide-y divide-gray-50">
          <div v-for="i in 5" :key="i" class="px-6 py-4 flex items-center gap-4">
            <div class="h-4 bg-gray-200 rounded flex-1" />
            <div class="h-4 bg-gray-200 rounded w-20" />
            <div class="h-4 bg-gray-200 rounded w-24" />
            <div class="h-6 bg-gray-100 rounded-full w-20" />
            <div class="h-4 bg-gray-200 rounded w-16" />
          </div>
        </div>
      </div>
    </div>

    <!-- ══ CONTENIDO REAL ══ -->
    <div v-else>
      <!-- Top Header with Quick Filters -->
      <div class="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
        <div class="flex items-center gap-3">
          <h1 class="text-2xl font-bold text-gray-900">Panel de Control</h1>
          <span class="hidden md:block text-gray-300">|</span>
          <div class="flex flex-wrap gap-2 text-sm">
            <Link href="/products" class="px-4 py-1.5 rounded-full bg-orange-500 text-white font-medium hover:bg-orange-600 transition-colors flex items-center gap-2">
              <i class="fa fa-box" /> Productos
            </Link>
            <Link href="/brands" class="px-4 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center gap-2">
              <i class="fa fa-tags" /> Marcas
            </Link>
            <Link href="/inventories" class="px-4 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center gap-2">
              <i class="fa fa-warehouse" /> Inventario
            </Link>
            <Link href="/orders" class="px-4 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center gap-2">
              <i class="fa fa-shopping-cart" /> Ventas
            </Link>
            <!-- <Link href="/quotes/create" class="px-4 py-1.5 rounded-full bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors flex items-center gap-2">
                    <i class="fa fa-file-invoice-dollar"></i> Crear Cotización
                 </Link> -->
            <Link href="/shoppings" class="px-4 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center gap-2">
              <i class="fa fa-shopping-bag" /> Compras y Gastos
            </Link>

            <button class="px-4 py-1.5 rounded-full bg-white border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors flex items-center gap-2" @click="showCatalogModal = true">
              <i class="fa fa-store text-orange-500" /> Catálogo
            </button>
          </div>
        </div>
        
        <Link href="/reports" class="text-orange-500 font-medium hover:text-orange-700 text-sm flex items-center gap-1">
          Ver detalles <i class="fa fa-arrow-right" />
        </Link>
      </div>

      <!-- ══ GUÍA Y CHECKLIST DE ONBOARDING ══ -->
      <onboarding-guide :onboarding="onboarding" :account="account" />

      <!-- ══ BANNERS SUPERIORES (PLAN) ══ -->
      <div v-if="['admin', 'sales'].includes(role)" class="hidden md:grid grid-cols-1 gap-6 mb-8 relative z-0">
        <!-- 1. Tarjeta de Plan (Condicional: Publicidad vs Status) -->
        <!-- ESTADO: PUBLICIDAD (Para usuarios Free) -->
        <div
          v-if="account.plan === 'free'" 
          class="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-50/90 via-white to-amber-50/60 p-6 shadow-sm border border-orange-200/90 group transition-all duration-300 hover:shadow-md hover:border-orange-300"
        >
          <!-- Decoración Luminosa Suave -->
          <div class="pointer-events-none absolute -right-12 -top-12 w-48 h-48 bg-orange-200/40 rounded-full blur-3xl group-hover:bg-orange-300/40 transition-all duration-500" />
          <div class="pointer-events-none absolute -left-10 -bottom-10 w-36 h-36 bg-amber-200/30 rounded-full blur-2xl transition-all duration-500" />
        
          <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div class="flex items-center gap-4 text-center md:text-left">
              <div class="w-12 h-12 rounded-xl bg-orange-100 border border-orange-200 text-orange-600 flex items-center justify-center flex-shrink-0 shadow-sm transition-transform duration-300 group-hover:scale-105">
                <i class="fas fa-crown text-xl text-orange-600" />
              </div>
              <div>
                <div class="flex items-center gap-2 justify-center md:justify-start mb-1">
                  <span class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-orange-100 border border-orange-200/80 text-orange-800 text-xs font-bold">
                    <i class="fas fa-gift text-orange-600 text-[10px]" />
                    Plan Free
                  </span>
                  <span class="text-xs text-gray-500 font-medium">&bull; Puedes escalar a Pro</span>
                </div>
                <h2 class="text-gray-900 font-extrabold text-base sm:text-lg leading-tight tracking-tight">
                  Potencia tu negocio con <span class="text-orange-600 font-black">Realgestión Pro</span>
                </h2>
                <p class="text-gray-600 text-xs sm:text-sm mt-1 font-normal leading-relaxed">
                  Desbloquea múltiples usuarios, reportes avanzados, control total de gastos y más.
                </p>
              </div>
            </div>
          
            <Link
              href="/subscription/prime"
              class="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-orange-600 text-white font-bold text-sm hover:bg-orange-700 transition-all shadow hover:shadow-md hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
            >
              <i class="fas fa-bolt text-xs text-orange-200" />
              <span>Descubrir Beneficios</span>
            </Link>
          </div>
        </div>

      <!-- ESTADO: STATUS (Para usuarios Prime/Pro) -->
      <!-- <div v-else 
           class="relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-gray-100 group">
        <div class="pointer-events-none absolute -right-10 -top-10 w-48 h-48 bg-orange-50 opacity-50 rounded-full blur-3xl group-hover:opacity-80 transition-opacity duration-700"></div>
        
        <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div class="flex items-center gap-4">
            <div class="w-14 h-14 rounded-2xl bg-orange-50 border border-orange-100 text-orange-500 flex items-center justify-center flex-shrink-0 shadow-sm transition-all duration-500 group-hover:scale-110">
              <i class="fas fa-crown text-2xl"></i>
            </div>
            
            <div>
              <p class="text-[10px] uppercase font-bold tracking-widest text-gray-400 mb-1">Tu Suscripción Activa</p>
              <h3 class="text-xl font-black text-gray-900 tracking-tight">{{ account.plan_name }}</h3>
              <div class="flex items-center gap-1.5 mt-1">
                 <span class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                 <span class="text-[10px] text-green-600 font-bold uppercase tracking-tighter">Cuenta Verificada</span>
              </div>
            </div>
          </div>
          
          <Link href="/subscription"
             class="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl bg-gray-50 text-gray-700 font-bold text-sm hover:bg-gray-100 transition-all border border-gray-200 active:scale-95">
            Gestionar suscripción
            <i class="fas fa-cog text-[10px] opacity-50"></i>
          </Link>
        </div>
      </div> -->
      </div>
    
      <!-- Estadísticas principales para administradores -->
      <div v-if="['financial', 'admin'].includes(role)" class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <!-- Cuentas por Cobrar -->
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
          <div class="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 mb-4">
            <i class="fa fa-wallet text-lg" />
          </div>
          <p class="text-slate-500 text-sm font-medium mb-1">Cuentas por Cobrar</p>
          <h3 class="text-3xl font-bold text-slate-800">{{ balanceTotal ? formatCurrency(balanceTotal) : '$ 0' }}</h3>
        </div>

        <!-- Ventas del Mes -->
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
          <div class="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-green-600 mb-4">
            <i class="fa fa-chart-line text-lg" />
          </div>
          <p class="text-slate-500 text-sm font-medium mb-1">Ventas del Mes</p>
          <h3 class="text-3xl font-bold text-slate-800">{{ orderMonthTotal ? formatCurrency(orderMonthTotal) : '$ 0' }}</h3>
        </div>

        <!-- Recaudado del Mes -->
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
          <div class="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600 mb-4">
            <i class="fa fa-money-bill-wave text-lg" />
          </div>
          <p class="text-slate-500 text-sm font-medium mb-1">Recaudado del Mes</p>
          <h3 class="text-3xl font-bold text-slate-800">{{ recaudadoTotal ? formatCurrency(recaudadoTotal) : '$ 0' }}</h3>
        </div>

        <!-- Utilidad -->
        <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
          <div class="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 mb-4">
            <i class="fa fa-chart-pie text-lg" />
          </div>
          <p class="text-slate-500 text-sm font-medium mb-1">Utilidad</p>
          <h3 class="text-3xl font-bold text-slate-800">{{ utilityOrderTotal ? formatCurrency(utilityOrderTotal) : '$ 0' }}</h3>
        </div>
      </div>

      <!-- Resumen Financiero del Mes -->
      <section v-if="role === 'admin'" class="mb-10">
        <h2 class="text-xl font-semibold text-gray-900 mb-6">Resumen Financiero del Mes</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Ingreso Recibido -->
          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
            <div class="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center text-green-600 mb-4">
              <i class="fa fa-arrow-down text-lg" />
            </div>
            <p class="text-slate-500 text-sm font-medium mb-1">Ingreso Recibido</p>
            <h3 class="text-3xl font-bold text-slate-800">{{ formatCurrency(recaudadoTotal || 0) }}</h3>
            <p class="text-xs text-gray-400 mt-2">Pagos recibidos este mes</p>
          </div>

          <!-- Total Gastado -->
          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1">
            <div class="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600 mb-4">
              <i class="fa fa-arrow-up text-lg" />
            </div>
            <p class="text-slate-500 text-sm font-medium mb-1">Total Gastos</p>
            <h3 class="text-3xl font-bold text-slate-800">{{ formatCurrency(spentTotal || 0) }}</h3>
            <p class="text-xs text-gray-400 mt-2">Gastos registrados este mes</p>
          </div>

          <!-- Capital Neto -->
          <div
            class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 transition-transform hover:-translate-y-1"
            :class="netCapital >= 0 ? '' : 'border-red-200'"
          >
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
              :class="netCapital >= 0 ? 'bg-teal-100 text-teal-600' : 'bg-red-100 text-red-600'"
            >
              <i class="fa fa-balance-scale text-lg" />
            </div>
            <p class="text-slate-500 text-sm font-medium mb-1">Capital Neto</p>
            <h3 class="text-3xl font-bold" :class="netCapital >= 0 ? 'text-teal-700' : 'text-red-600'">
              {{ formatCurrency(netCapital) }}
            </h3>
            <p class="text-xs mt-2" :class="netCapital >= 0 ? 'text-teal-500' : 'text-red-400'">
              {{ netCapital >= 0 ? 'Superávit del período' : 'Déficit del período' }}
            </p>
          </div>
        </div>

        <!-- Spending Progress Bar -->
        <div v-if="recaudadoTotal > 0" class="mt-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div class="flex justify-between items-center mb-3">
            <span class="text-sm font-semibold text-gray-700">Gastos vs Ingresos</span>
            <span class="text-sm font-bold" :class="spentRatio > 100 ? 'text-red-600' : spentRatio > 75 ? 'text-orange-500' : 'text-teal-600'">
              {{ spentRatio }}%
            </span>
          </div>
          <div class="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div
              class="h-3 rounded-full transition-all duration-500"
              :class="spentRatio > 100 ? 'bg-red-500' : spentRatio > 75 ? 'bg-orange-400' : 'bg-teal-500'"
              :style="{ width: Math.min(spentRatio, 100) + '%' }"
            />
          </div>
          <div class="flex justify-between text-xs text-gray-400 mt-2">
            <span>$0</span>
            <span>{{ formatCurrency(recaudadoTotal) }}</span>
          </div>
        </div>
      </section>

      <!-- Estadísticas de productos -->
      <section v-if="role === 'admin' && ['financial', 'admin'].includes(role)" class="mb-10">
        <h2 class="text-xl font-bold text-gray-900 mb-6">Estadísticas de Productos</h2>
        <div class="grid grid-cols-1 gap-6 md:grid-cols-4">
          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p class="text-slate-500 text-sm font-medium mb-2">Total Referencias</p>
            <h3 class="text-3xl font-bold text-slate-800">{{ products ? products.toString() : '0' }}</h3>
          </div>

          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p class="text-slate-500 text-sm font-medium mb-2">Costo Total Inventario</p>
            <h3 class="text-3xl font-bold text-slate-800">{{ productsStats?.costTotal ? formatCurrency(productsStats.costTotal) : '$ 0' }}</h3>
          </div>

          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p class="text-slate-500 text-sm font-medium mb-2">Costo Total Venta</p>
            <h3 class="text-3xl font-bold text-slate-800">{{ productsStats?.priceTotal ? formatCurrency(productsStats.priceTotal) : '$ 0' }}</h3>
          </div>

          <div class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p class="text-slate-500 text-sm font-medium mb-2">Utilidad en Productos</p>
            <h3 class="text-3xl font-bold text-slate-800">{{ productsStats?.utilityTotal ? formatCurrency(productsStats.utilityTotal) : '$ 0' }}</h3>
          </div>
        </div>
      </section>

      <!-- Tabla de últimas órdenes -->
      <section v-if="['admin', 'sales'].includes(role)">
        <!-- Header + controles -->
        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
          <h2 class="text-xl font-bold text-gray-900">Últimas Ventas</h2>
          <div class="flex items-center gap-2 w-full sm:w-auto">
            <!-- Búsqueda -->
            <div class="relative flex-1 sm:w-56">
              <i class="material-icons-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style="font-size:16px;">search</i>
              <input
                v-model="orderSearch"
                type="text"
                placeholder="Cliente o N° orden..."
                class="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-all"
              />
            </div>

            <!-- Filtro de estado -->
            <div class="relative">
              <select
                v-model="orderStateFilter"
                class="appearance-none pl-3 pr-8 py-2 text-sm bg-white border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 transition-all cursor-pointer text-gray-600"
              >
                <option value="">Todos</option>
                <option value="request">Pendiente</option>
                <option value="pay">Pagada</option>
                <option value="finish">Finalizada</option>
                <option value="delivery">Entregada</option>
                <option value="estimate">Presupuesto</option>
              </select>
              <i class="material-icons-outlined absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" style="font-size:16px;">expand_more</i>
            </div>
          </div>
        </div>
      
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full whitespace-nowrap text-left text-sm">
              <thead class="bg-gray-50/50 border-b border-gray-100">
                <tr>
                  <th v-for="header in tableHeaders" :key="header" class="px-6 py-4 font-bold text-gray-400 uppercase tracking-wider text-xs">
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-50">
                <tr v-for="order in filteredOrders" :key="order.id" class="hover:bg-gray-50 transition-colors group">
                  <td class="px-6 py-4">
                    <Link :href="`orders/${order.id}`" class="font-medium text-gray-600 group-hover:text-orange-500">
                      {{ formatDate(order.created_at) }}
                    </Link>
                  </td>
                  <td class="px-6 py-4">
                    <Link :href="`orders/${order.id}`" class="text-gray-900 font-medium">
                      {{ order.number_order }}
                    </Link>
                  </td>
                  <td class="px-6 py-4">
                    <Link :href="`orders/${order.id}`" class="text-gray-600">
                      {{ order.client?.name }}
                    </Link>
                  </td>
                  <td class="px-6 py-4">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize" 
                      :class="getOrderStateClass(order.order_state)"
                    >
                      {{ getOrderStateLabel(order.order_state) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 text-gray-500">
                    {{ formatFullName(order.responsible) }}
                  </td>
                  <td class="px-6 py-4 text-gray-500">
                    {{ formatCurrency(order.balance) }}
                  </td>
                  <td class="px-6 py-4 font-bold text-gray-900">
                    {{ formatCurrency(order.total) }}
                  </td>
                </tr>
                <tr v-if="!filteredOrders.length">
                  <td colspan="7" class="px-6 py-12 text-center text-gray-400">
                    <i class="material-icons-outlined text-3xl text-gray-200 mb-2 block">search_off</i>
                    <p class="italic text-sm">
                      {{ orderSearch || orderStateFilter ? 'No hay resultados para tu búsqueda.' : 'No hay ventas registradas recientemente.' }}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Modal de Catálogo -->
      <teleport to="body">
        <div v-if="showCatalogModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300" @click="showCatalogModal = false" />
        
          <div class="relative w-full max-w-lg bg-white rounded-[2.5rem] overflow-hidden shadow-2xl transform transition-all duration-300 animate-in fade-in zoom-in slide-in-from-bottom-8">
            <!-- Banner con diseño premium anterior -->
            <div class="relative overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 p-8 md:p-10">
              <div class="pointer-events-none absolute -right-20 -top-20 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl" />
              <div class="pointer-events-none absolute -left-20 -bottom-20 w-64 h-64 bg-orange-400 opacity-20 rounded-full blur-3xl" />
            
              <button class="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-black/10 text-white hover:bg-black/20 transition-all active:scale-90 z-20" @click="showCatalogModal = false">
                <i class="fas fa-times" />
              </button>

              <div class="relative z-10">
                <div class="flex items-center gap-5 mb-8">
                  <div class="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-xl">
                    <i class="fas fa-store text-white text-3xl" />
                  </div>
                  <div>
                    <h3 class="text-white font-black text-3xl leading-tight tracking-tight">Catálogo Digital</h3>
                    <div class="flex items-center gap-2 mt-1">
                      <span class="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <p class="text-orange-50 text-[10px] font-black uppercase tracking-[0.2em] opacity-90">¡Tienda Online Activa!</p>
                    </div>
                  </div>
                </div>

                <div class="bg-black/10 backdrop-blur-md rounded-2xl p-5 border border-white/10 mb-8 group transition-all hover:bg-black/20">
                  <p class="text-orange-100 text-[10px] uppercase font-black mb-3 opacity-70 tracking-widest">Enlace Directo</p>
                  <div class="flex items-center gap-4">
                    <div class="flex-1 overflow-hidden">
                      <code class="text-base text-white font-mono font-bold truncate block">{{ catalogUrl }}</code>
                    </div>
                    <button class="w-10 h-10 flex items-center justify-center bg-white rounded-xl text-orange-600 hover:bg-orange-50 transition-all shadow-lg active:scale-90 flex-shrink-0" @click="copyCatalogUrl">
                      <i :class="copied ? 'fas fa-check text-green-500' : 'fas fa-copy'" class="text-sm" />
                    </button>
                  </div>
                </div>

                <div class="flex items-center gap-4">
                  <a
                    :href="catalogUrl" target="_blank"
                    class="flex-1 inline-flex items-center justify-center gap-3 py-4 rounded-2xl bg-white text-orange-600 font-black text-sm hover:bg-orange-50 transition-all shadow-xl hover:-translate-y-1 active:scale-95 group"
                  >
                    <i class="fas fa-external-link-alt transition-transform group-hover:rotate-12" />
                    Ir a la Tienda
                  </a>
                  <Link
                    v-if="role === 'admin'" :href="catalogConfigUrl"
                    class="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-900/40 text-white hover:bg-gray-900 transition-all border border-white/10 shadow-xl group"
                  >
                    <i class="fas fa-cog text-xl transition-transform group-hover:rotate-90" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </teleport>
    </div> <!-- /v-else -->
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import OnboardingGuide from '@/Pages/Dashboard/Partials/OnboardingGuide'

export default {
  components: {
    Link,
    Head,
    OnboardingGuide,
  },
  layout: Layout,
  
  props: {
    onboarding: Object,
    total: Number,
    orders: Object,
    clientTotal: Number,
    balanceTotal: Number,
    products: Number,
    orderTotal: Number,
    spentTotal: Number,
    productsSoldout: Object,
    orderMonthTotal: Number,
    productsStats: Object,
    devolutions: Object,
    recaudadoTotal: Number,
    utilityOrderTotal: Number,
  },

  data() {
    const user = this.$page.props.auth.user
    return {
      role: user.role,
      accountId: user.account.id,
      account: user.account,
      copied: false,
      showCatalogModal: false,
      orderSearch: '',
      orderStateFilter: '',
      tableHeaders: [
        'Fecha',
        'Número',
        'Cliente', 
        'Estado',
        'Vendedor',
        'Abonado',
        'Total',
      ],
    }
  },

  computed: {
    isLoading() {
      return this.$inertia?.processing ?? false
    },
    filteredOrders() {
      let list = this.orders?.data ?? []
      if (this.orderStateFilter) {
        list = list.filter(o => o.order_state === this.orderStateFilter)
      }
      if (this.orderSearch.trim()) {
        const q = this.orderSearch.trim().toLowerCase()
        list = list.filter(o =>
          o.client?.name?.toLowerCase().includes(q) ||
          o.number_order?.toLowerCase().includes(q),
        )
      }
      return list
    },
    catalogUrl() {
      const account = this.$page.props.auth.user.account
      if (account.username && account.catalog_domain) {
        const protocol = window.location.protocol
        return `${protocol}//${account.username}.${account.catalog_domain}`
      }
      return `${window.location.origin}/catalogo/${this.accountId}`
    },
    catalogConfigUrl() {
      return `/accounts/${this.accountId}`
    },
    netCapital() {
      return (this.recaudadoTotal || 0) - (this.spentTotal || 0)
    },
    spentRatio() {
      if (!this.recaudadoTotal || this.recaudadoTotal === 0) return 0
      return Math.round((this.spentTotal / this.recaudadoTotal) * 100 * 10) / 10
    },
  },

  methods: {
    async copyCatalogUrl() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.catalogUrl)
          this.copied = true
          setTimeout(() => { this.copied = false }, 2000)
        } else {
          throw new Error('Clipboard API unavailable')
        }
      } catch (err) {
        // Fallback for non-secure contexts or unsupported browsers
        const el = document.createElement('textarea')
        el.value = this.catalogUrl
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        const success = document.execCommand('copy')
        document.body.removeChild(el)
        if (success) {
          this.copied = true
          setTimeout(() => { this.copied = false }, 2000)
        }
      }
    },
    formatDate(date) {
      // Basic formatting, could be improved with libraries like date-fns
      return new Date(date).toLocaleDateString('es-VE')
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount)
    },

    formatFullName(user) {
      if (!user) return ''
      return `${user.first_name}` // Just first name to keep it compact as in design
    },

    getOrderStateLabel(state) {
      const labels = {
        cancel: 'Anulada',
        request: 'Pendiente',
        finish: 'Finalizada',
        pay: 'Pagada',
        estimate: 'Presupuesto',
        delivery: 'Entregada',
        por_iniciar: 'Por iniciar',
        en_preparacion: 'En preparación',
        pendiente_pago: 'Pendiente por pago',
        finalizado: 'Finalizado',
      }
      return labels[state] || state
    },

    getOrderStateClass(state) {
      const classes = {
        cancel: 'bg-red-50 text-red-700',
        request: 'bg-yellow-50 text-yellow-700',
        finish: 'bg-green-50 text-green-700',
        pay: 'bg-blue-50 text-blue-700',
        estimate: 'bg-gray-100 text-gray-700',
        delivery: 'bg-purple-50 text-purple-700',
        por_iniciar: 'bg-amber-50 text-amber-700 border border-amber-200',
        en_preparacion: 'bg-blue-50 text-blue-700 border border-blue-200',
        pendiente_pago: 'bg-purple-50 text-purple-700 border border-purple-200',
        finalizado: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      }
      return classes[state] || 'bg-gray-100 text-gray-800'
    },
  },
}
</script>
